(function () {
  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    // Plain page navigation only gets the iris once per session
    if (!transitionType && irisAlreadyPlayed()) {
      viewTransition.skipTransition();
      return;
    }

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      sessionStorage.setItem('custom-transition-type', transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      sessionStorage.removeItem('custom-transition-type');
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    // Plain page navigation only gets the iris once per session
    if (!customTransitionType && irisAlreadyPlayed()) {
      viewTransition.skipTransition();
      return;
    }
    if (!customTransitionType) markIrisPlayed();

    // For the plain page-navigation iris, hold the incoming page's own fade-up
    // reveals until the circle is almost fully open, so they "kick in" as the
    // iris finishes instead of playing hidden behind the snapshot. Must run here,
    // synchronously, so the hidden state is baked into the captured -new snapshot.
    let releaseReveal = null;
    if (!customTransitionType && !prefersReducedMotion()) {
      releaseReveal = holdContentReveal();
    }
    if (releaseReveal) {
      let done = false;
      const run = () => {
        if (done) return;
        done = true;
        releaseReveal();
      };
      // Start ~0.2s before the iris finishes so the fade-up is already underway
      // as the circle disappears. Falls back to `finished` if the VT is skipped.
      viewTransition.ready.then(() => setTimeout(run, IRIS_REVEAL_DELAY_MS), run);
      viewTransition.finished.then(run);
    }

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      await viewTransition.finished;

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /* Iris total is ~2.5s (1.05s close + 0.4s hold + 1.05s open, see
     theme-styles-variables.liquid). Release the held reveals ~0.2s before the
     open finishes so they animate in just as the circle disappears. */
  const IRIS_REVEAL_DELAY_MS = 2300;

  /* The full-page iris plays ONCE per browser session (the brand moment).
     After that, plain page navigations skip the view transition entirely -
     with hover prefetch (sk-prefetch.js) the swap is near-instant, and
     sk-nav-progress.js covers any residual wait. Element-level transitions
     (e.g. the PDP product-image morph) are functional and always play. */
  const IRIS_DONE_KEY = 'sk-iris-done';
  function irisAlreadyPlayed() {
    try { return sessionStorage.getItem(IRIS_DONE_KEY) === '1'; } catch (e) { return false; }
  }
  function markIrisPlayed() {
    try { sessionStorage.setItem(IRIS_DONE_KEY, '1'); } catch (e) { /* no-op */ }
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Hide the above-the-fold fade-up targets on the incoming page (the same
   * elements sk-scroll-anim.liquid animates, which it otherwise leaves static
   * when already in view). Returns a function that fades them back in with a
   * stagger, or null if there's nothing to hold.
   * @returns {(() => void) | null}
   */
  function holdContentReveal() {
    const SEL = 'product-card, .sk-ad-slot, .sk-essay-band, .sk-review-band';
    const main = document.getElementById('MainContent') || document.body;
    const vh = window.innerHeight;
    const candidates = main.querySelectorAll(SEL);
    const els = [];

    // Read-only pass (single layout flush): collect just the elements the iris
    // reveals (in the initial viewport). Elements are in DOM order, so once one
    // starts below the fold we can stop, and we cap to keep the work tiny.
    for (let i = 0; i < candidates.length && els.length < 16; i++) {
      const rect = candidates[i].getBoundingClientRect();
      if (rect.top >= vh - 20) break;
      if (rect.bottom > 0) els.push(candidates[i]);
    }

    if (!els.length) return null;

    els.forEach((el) => {
      el.style.setProperty('opacity', '0', 'important');
      el.style.setProperty('transform', 'translateY(28px)', 'important');
      el.style.setProperty('transition', 'none', 'important');
      el.style.setProperty('will-change', 'opacity, transform');
    });

    return () => {
      // Re-enable transition (with a per-element stagger) one frame before
      // setting the end state, so the browser animates rather than snapping.
      requestAnimationFrame(() => {
        els.forEach((el, i) => {
          const delay = (i * 0.07).toFixed(2);
          el.style.setProperty(
            'transition',
            `opacity .5s ease ${delay}s, transform .5s ease ${delay}s`,
            'important'
          );
        });
        requestAnimationFrame(() => {
          els.forEach((el) => {
            el.style.setProperty('opacity', '1', 'important');
            el.style.setProperty('transform', 'none', 'important');
          });
        });
      });
    };
  }

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !(viewTransition instanceof ViewTransition) || isLowPowerDevice();
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
